import { ContainerComponent, StepHeader, InsuredPersonsForm, FormRef, NavigationButtons, LoaderSquare, Flex } from "src/components"
import { NavigationService } from "src/common/utils/navigation.util";
import { ContractorForm, ContractorFormItemType, ContractorFormValues } from "src/components/forms/contractor.form"
import { DataConsentForm } from "src/components/forms/dataConsent.form"
import { InsuredPersonFormItemType, InsuredPersonsFormValues } from "src/components/forms/insuredPerson.form"
import { useEffect, useRef, useState } from "react"
import { useGeneralSoContext, useGetOfferById, showMessage, handleErrorMessage, useLoader, useAcceptTerms, useCalculationInput, PersonType, useNavigationPath, BrandEnum } from "src/common"
import { useGetSo } from "src/common/api/apollo/queries/useGetSo.query"
import { ContractorInput, GetSoDto, InsuredPersonInput } from "src/common"
import { Helmet } from "react-helmet-async"
import { useParams } from "react-router-dom";

export const OfferConfirmationPage = () => {
  const { offerId: offerIdFromUrl } = useParams();
  const { forwardPath, backwardPath } = useNavigationPath();
  const contractorRef = useRef<FormRef>(null);
  const insuredPersonRef = useRef<FormRef>(null);
  const dataConsentRef = useRef<FormRef>(null);

  const { soId, offerId, soInitialData, setSoInitialData, setSoId, setOfferId } = useGeneralSoContext();
  const { so, loading, refetch } = useGetSo();
  const { offer, refetch: refetchOffer, loading: offerLoading } = useGetOfferById(offerIdFromUrl);

  const { startLoader, stopLoader } = useLoader();
  const [acceptTerms] = useAcceptTerms();
  const { calculationInput } = useCalculationInput();

  const [sameAsInsured, setSameAsInsured] = useState(true);
  const [contractorType, setContractorType] = useState(PersonType.Physical);
  const [insuredType, setInsuredType] = useState(PersonType.Physical);

  const propertyInfo = so?.productTypeSpecific?.propertyInfo;
  const brand = offer?.productTypeSpecific?.brandId

  useEffect(() => {
    if (offerIdFromUrl) setOfferId(offerIdFromUrl);
    refetchOffer();
  }, [])

  useEffect(() => {
    if (offer && !so) {
      refetch({ id: offer.soId });
      setSoId(offer.soId);
    }
  }, [offerLoading]);

  useEffect(() => {
    if (so) {
      setSameAsInsured(so?.productTypeSpecific?.contractor?.sameAsInsured || true);
      setContractorType(so?.productTypeSpecific?.contractor?.type || PersonType.Physical);
      setInsuredType(so?.productTypeSpecific?.insured?.type ?? PersonType.Physical);
      setSoInitialData({ ...soInitialData, propertyInfo: propertyInfo });
    }
  }, [loading]);

  const handleContractorAddress = (same: boolean) => {
    const source = same ? propertyInfo : so?.productTypeSpecific?.contractor;
    const addressData = {
      city: source?.city || '',
      cityKompareId: source?.cityKompareId || '',
      street: source?.street || '',
      streetKompareId: source?.streetKompareId || '',
      houseNumber: source?.houseNumber || '',
      zipCode: source?.zipCode || 0,
    };
    Object.entries(addressData).forEach(([key, value]) => {
      contractorRef.current?.setFieldValue?.(key, value);
    });

    if (sameAsInsured) {
      insuredPersonRef.current?.setFieldValue?.('sameAddressAsProperty', same);
      handleInsuredAddress(same);
    }
  };

  const handleInsuredAddress = (same: boolean) => {
    const source = same ? propertyInfo : so?.productTypeSpecific?.insured;
    const addressData = {
      city: source?.city || '',
      cityKompareId: source?.cityKompareId || '',
      street: source?.street || '',
      streetKompareId: source?.streetKompareId || '',
      houseNumber: source?.houseNumber || '',
      zipCode: source?.zipCode || 0,
    };
    Object.entries(addressData).forEach(([key, value]) => {
      insuredPersonRef.current?.setFieldValue?.(key, value);
    });
  };

  const handleBack = () => {
    NavigationService.navigate(backwardPath.replace(':soId', soId));
  };

  const mapContractorToInput = (values: ContractorFormValues): ContractorInput => ({
    type: values.type,
    sameAsInsured: values.sameAsInsured,
    sameAddressAsProperty: values.sameAddressAsProperty,
    firstName: values.firstName,
    lastName: values.lastName,
    companyName: values.companyName,
    dateOfBirth: values.birthDate,
    oib: values.oib,
    city: values.city,
    cityKompareId: values.cityKompareId,
    street: values.street,
    streetKompareId: values.streetKompareId,
    zipCode: values.zipCode,
    houseNumber: values.houseNumber,
    email: values.email,
    phoneNumber: values.phoneNumber
  });

  const mapInsuredToInput = (values: InsuredPersonsFormValues): InsuredPersonInput => ({
    type: values.type,
    sameAddressAsProperty: values.sameAddressAsProperty,
    firstName: values.firstName,
    lastName: values.lastName,
    companyName: values.companyName,
    dateOfBirth: values.birthDate,
    oib: values.oib,
    city: values.city,
    street: values.street,
    houseNumber: values.houseNumber,
    email: values.email,
    phoneNumber: values.phoneNumber
  });

  const handleSubmit = async () => {
    const isDataConsentValid = dataConsentRef.current?.validateForm?.();
    if (!isDataConsentValid) return;

    const isContractorValid = contractorRef.current?.validateForm?.();
    const isInsuredValid = sameAsInsured || insuredPersonRef.current?.validateForm?.();
    if (!isContractorValid || !isInsuredValid) return;

    const contractorValues = contractorRef.current?.getValues?.() as ContractorFormValues;
    const insuredValues = insuredPersonRef.current?.getValues?.() as InsuredPersonsFormValues;

    const contractor = mapContractorToInput(contractorValues);
    const insured = contractor.sameAsInsured ? contractor : mapInsuredToInput(insuredValues);

    setSoInitialData({ ...soInitialData, contractor, insured });

    startLoader();
    try {
      await acceptTerms({
        contractor,
        insured,
      },
      {
        ...calculationInput!,
        brandCode: offer?.productTypeSpecific.brandId.code,
        packageCode: offer?.productTypeSpecific?.calculationData?.packageCode,
        nonStandardRequestToggle: offer?.productTypeSpecific?.calculationData?.nonStandardRequestToggle,
      },
      {
        generalTOSConsent: true,
        iddConsent: true,
        emailDocumentationConsent: true,
        fourteenDayCancellationPolicy: true,
        allianzGDPR: brand?.code === BrandEnum.ALLIANZ,
        uniqaTermsAccepted: brand?.code === BrandEnum.UNIQA,
        validDataConsent: true
      });
      NavigationService.navigate(
        forwardPath
          .replace(':soId', `${soId}`)
          .replace(':offerId', `${offerId}`)
      );
    } catch (error) {
      showMessage({
        type: 'error',
        message: handleErrorMessage(error),
        duration: 5000,
        fixed: true,
      });
    } finally {
      stopLoader();
    }
  };

  const modelToContractorForm = (so?: GetSoDto): ContractorFormValues => ({
    type: contractorType,
    sameAsInsured,
    sameAddressAsProperty: so?.productTypeSpecific?.contractor?.sameAddressAsProperty ?? false,
    firstName: so?.productTypeSpecific?.contractor?.firstName ?? '',
    lastName: so?.productTypeSpecific?.contractor?.lastName ?? '',
    companyName: so?.productTypeSpecific?.contractor?.companyName ?? '',
    birthDate: so?.productTypeSpecific?.contractor?.dateOfBirth ?? '',
    oib: so?.productTypeSpecific?.contractor?.oib ?? '',
    city: so?.productTypeSpecific?.contractor?.city ?? '',
    cityKompareId: so?.productTypeSpecific?.contractor?.cityKompareId ?? '',
    street: so?.productTypeSpecific?.contractor?.street ?? '',
    streetKompareId: so?.productTypeSpecific?.contractor?.streetKompareId ?? '',
    zipCode: so?.productTypeSpecific?.contractor?.zipCode ?? 0,
    houseNumber: so?.productTypeSpecific?.contractor?.houseNumber ?? '',
    email: so?.productTypeSpecific?.contractor?.email ?? '',
    phoneNumber: so?.productTypeSpecific?.insured?.phoneNumber ?? '',
  });

  const modelToInsuredForm = (so?: GetSoDto): InsuredPersonsFormValues => ({
    type: insuredType,
    sameAddressAsProperty: so?.productTypeSpecific?.insured?.sameAddressAsProperty ?? false,
    firstName: so?.productTypeSpecific?.insured?.firstName ?? '',
    lastName: so?.productTypeSpecific?.insured?.lastName ?? '',
    companyName: so?.productTypeSpecific?.insured?.companyName ?? '',
    birthDate: so?.productTypeSpecific?.insured?.dateOfBirth ?? '',
    oib: so?.productTypeSpecific?.insured?.oib ?? '',
    city: so?.productTypeSpecific?.insured?.city ?? '',
    cityKompareId: so?.productTypeSpecific?.insured?.cityKompareId ?? '',
    street: so?.productTypeSpecific?.insured?.street ?? '',
    streetKompareId: so?.productTypeSpecific?.insured?.streetKompareId ?? '',
    houseNumber: so?.productTypeSpecific?.insured?.houseNumber ?? '',
    zipCode: so?.productTypeSpecific?.insured?.zipCode ?? 0,
    email: so?.productTypeSpecific?.insured?.email ?? '',
    phoneNumber: so?.productTypeSpecific?.insured?.phoneNumber ?? '',
  });


  const contractorData = modelToContractorForm(so || undefined);
  const insuredPersonData = modelToInsuredForm(so || undefined);

  if (loading) return (
    <ContainerComponent>
      <Flex align="center" justify="center" style={{ height: '40vh' }}>
        <LoaderSquare/>
      </Flex>
    </ContainerComponent>
  );

  const contractorItems: Array<ContractorFormItemType> = contractorType === PersonType.Physical ? ['type', 'sameAddressAsProperty', 'sameAsInsured', 'firstName', 'lastName', 'birthDate', 'oib', 'city', 'street', 'zipCode', 'houseNumber', 'email', 'phoneNumber']
    : ['type', 'sameAddressAsProperty', 'sameAsInsured', 'companyName', 'oib', 'city', 'street', 'zipCode', 'houseNumber', 'sameAsInsured']

  const insuredItems: Array<InsuredPersonFormItemType> = insuredType === PersonType.Physical ?  ['type', 'sameAddressAsProperty', 'firstName', 'lastName', 'birthDate', 'oib', 'city', 'street', 'houseNumber', 'zipCode', 'email', 'phoneNumber']
  :  ['type', 'sameAddressAsProperty', 'companyName', 'oib', 'city', 'street', 'houseNumber', 'zipCode'];

  return (
    <ContainerComponent>
      <Helmet>
        <title>Potvrda podataka | Osiguranje imovine - kompare.hr</title>
      </Helmet>
      <StepHeader />
      <ContractorForm
        ref={contractorRef}
        setSameAsInsured={setSameAsInsured}
        setSameAddressAsProperty={handleContractorAddress}
        setContractorType={setContractorType}
        formItems={contractorItems}
        onSubmit={handleSubmit}
        contractorData={contractorData}
      />
      { !sameAsInsured && <InsuredPersonsForm
        formItems={insuredItems}
        setSameAddressAsProperty={handleInsuredAddress}
        setInsuredType={setInsuredType}
        ref={insuredPersonRef}
        insuredPersonData={insuredPersonData}
      /> }
      <DataConsentForm ref={dataConsentRef} offerId={offer?._id} brandCode={brand?.code} />
      <NavigationButtons
        handleBack={handleBack}
        handleNext={handleSubmit}
        gap={64}
      />
    </ContainerComponent>
  )
}
